import React from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../i18n';

const Hero = () => {
  const { t } = useLanguage();

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E9782A]/10 via-white to-[#E9782A]/5"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23E9782A' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 bg-[#E9782A]/10 px-4 py-2 rounded-full animate-in fade-in slide-in-from-left duration-500">
              <span className="w-2 h-2 bg-[#E9782A] rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-[#E9782A]">
                {t.hero.badge}
              </span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight animate-in fade-in slide-in-from-left duration-700">
              {t.hero.titlePre}{' '}
              <span className="text-[#E9782A] relative">
                {t.hero.titleHighlight}
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  height="12"
                  viewBox="0 0 200 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 10C60 3 140 3 198 10"
                    stroke="#E9782A"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed animate-in fade-in slide-in-from-left duration-900">
              {t.hero.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-in fade-in slide-in-from-left duration-1000">
              <Button
                data-testid="hero-start-campaign-button"
                size="lg"
                className="bg-[#E9782A] hover:bg-[#d96820] text-white px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-200 hover:shadow-xl hover:scale-105 group"
                onClick={scrollToContact}
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              
              <Button
                data-testid="hero-success-stories-button"
                size="lg"
                variant="outline"
                className="border-2 border-[#E9782A] text-[#E9782A] hover:bg-[#E9782A]/5 px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-200 group"
                onClick={() => {
                  const element = document.querySelector('#portfolio');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <Play className="mr-2 group-hover:scale-110 transition-transform" size={20} />
                {t.hero.ctaSecondary}
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 animate-in fade-in slide-in-from-left duration-1100">
              <div>
                <div className="text-3xl font-bold text-[#E9782A]" data-testid="hero-influencers-stat">133</div>
                <div className="text-sm text-gray-600 mt-1">{t.hero.statInfluencers}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#E9782A]">45M+</div>
                <div className="text-sm text-gray-600 mt-1">{t.hero.statReach}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#E9782A]">250+</div>
                <div className="text-sm text-gray-600 mt-1">{t.hero.statCampaigns}</div>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative animate-in fade-in slide-in-from-right duration-1000">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1655720357872-ce227e4164ba"
                alt="African professionals collaborating"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#E9782A]/20 to-transparent"></div>
            </div>

            {/* Floating Card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs animate-in fade-in zoom-in duration-1200">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-[#E9782A]/10 flex items-center justify-center">
                  <span className="text-2xl">🎯</span>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">45M+</div>
                  <div className="text-sm text-gray-600">{t.hero.statReach}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
